import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, ArrowRightIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SectionHeading } from '../components/SectionHeading';
import { OperatingRegions } from '../components/OperatingRegions';
import { company } from '../data/company';
const teamDesks = [{
  title: 'Sourcing & concessions',
  text: 'Field buyers based in Kolwezi, Likasi and Butembo who work directly with licensed cooperatives and semi-industrial operators.'
}, {
  title: 'Assay & quality control',
  text: 'On-site fire assay and XRF, with independent verification by SGS or Alfred H. Knight before any lot is released.'
}, {
  title: 'Documentation & compliance',
  text: 'CEEC and ARECOMS filings, Kimberley Process certificates, certificates of origin and ITSCI traceability records.'
}, {
  title: 'Logistics & freight',
  text: 'Sealed-container corridors, bonded warehousing, marine insurance and vessel nomination across four ports.'
}];
export function About() {
  return <>
      <PageHeader eyebrow={`Mining & Trading Company · Established ${company.established} · Lubumbashi, D.R. Congo`} title="A Congolese mining and trading company built on verification" intro="Lumumbashi Minerals SPRL began as a Katangan copper concentrate trader and grew into a mining and trading house exporting precious metals, copper units, rough diamonds and coloured gemstones — serving verified buyers in 38 countries." image={company.images.hero} imageAlt="Copper mining operation in Haut-Katanga, Democratic Republic of Congo" data-id="element-230" data-mp="1pj8gav2zr3ft" />

      <section className="mx-auto max-w-shell px-6 py-20" data-id="element-231" data-mp="2i7mhfjpdmfvl">
        <div className="grid gap-14 lg:grid-cols-[1.15fr_1fr] lg:items-start" data-id="element-232" data-mp="2i85yky77d08k">
          <div data-id="element-233" data-mp="2w7xa8sa8y4je">
            <SectionHeading eyebrow="Who we are" title="Mining and trading, from the pit to the port" intro="We operate where most exporters do not: at the source. Our buyers sit at the concession gate, our assayers test before money moves, and our logistics team keeps the cargo under one seal until it reaches the destination warehouse." data-id="element-234" data-mp="2w7f493xs8bya" />
            <div className="mt-8 space-y-5 text-[15px] leading-relaxed text-muted" data-id="element-235" data-mp="1xtrmavihb5vn">
              <p data-id="element-236" data-mp="22mt69hxjlvm3">
                Registered in Lubumbashi in {company.established}, the company was formed to bring
                contractual discipline to a market where informal handling had long made international
                buyers cautious. Sixteen years later we run two offices — the head office in
                Lubumbashi, Haut-Katanga, and the gemstone and diamond trading desk at Av. Bungulu
                N&#8217;11, Ville de Butembo, Nord Kivu.
              </p>
              <p data-id="element-237" data-mp="m8upajaaw219">
                Our export programme covers gold bullion and dore, 999 fine silver, LME Grade A copper
                cathodes, copper concentrates between 20% and 32% Cu, Kimberley Process certified
                rough diamonds and graded coloured gemstone lots. Buyers include smelters, copper
                mills, manufacturers, gem houses and industrial traders across Asia, Europe, the Gulf
                and the Americas.
              </p>
              <p data-id="element-238" data-mp="4yqshictaxp6">
                Every contract carries an assay clause, a declared penalty schedule where relevant, and
                a documentation set built to survive due diligence at the destination. That is what we
                sell as much as the mineral itself.
              </p>
            </div>
          </div>

          <div className="border border-hair bg-panel" data-id="element-239" data-mp="sn09m6z2yx9z">
            <img src={company.images.ceo} alt={`${company.ceo}, Chief Executive Officer`} className="aspect-[4/5] w-full object-cover" data-id="element-240" data-mp="omgch3eibtnu" />
            <div className="p-6" data-id="element-241" data-mp="3dbxqj2ips998">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold" data-id="element-242" data-mp="39cfgx2wcfklp">
                Chief Executive Officer
              </p>
              <h2 className="mt-2 font-display text-2xl text-bone" data-id="element-243" data-mp="112o0v01e6ck6">{company.ceo}</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted" data-id="element-244" data-mp="15wa5c4xeja4w">
                &ldquo;Buyers do not take a risk on minerals — they take a risk on the counterparty.
                Our job is to remove that risk with verified grade, complete documentation and cargo
                that arrives exactly as contracted.&rdquo;
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-hair bg-panel2/40 py-20" data-id="element-245" data-mp="1oss599memgc8">
        <div className="mx-auto max-w-shell px-6" data-id="element-246" data-mp="1asqwz93t86mq">
          <SectionHeading eyebrow="Our history" title="Sixteen years of export growth" data-id="element-247" data-mp="1aw50wls4gnwa" />
          <ol className="mt-12 grid gap-px border border-hair bg-hair sm:grid-cols-2 lg:grid-cols-3" data-id="element-248" data-mp="1exgnsjjm2v0s">
            {company.timeline.map(item => <li key={item.year} className="flex flex-col bg-ink p-7" data-id="element-249" data-mp="3h9ycdt3qljm">
                <span className="font-mono text-sm text-gold" data-id="element-250" data-mp="3s1tx01k2bsr9">{item.year}</span>
                <h3 className="mt-3 font-display text-2xl leading-snug text-bone" data-id="element-251" data-mp="3s3wvzb370w84">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-252" data-mp="3n5d5cxdnmw9f">{item.text}</p>
              </li>)}
          </ol>
        </div>
      </section>

      <OperatingRegions data-id="element-253" data-mp="x3sg0kxgl0f" />

      <section className="mx-auto max-w-shell px-6 py-20" data-id="element-254" data-mp="tppor8cq9sg">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.15fr] lg:items-center" data-id="element-255" data-mp="39cnjqu21ftcu">
          <div data-id="element-256" data-mp="34d14yg6ka51x">
            <img src={company.images.lab} alt="Assay laboratory technician analysing ore samples" className="w-full border border-hair object-cover" loading="lazy" data-id="element-257" data-mp="34dkdx7e8hj3t" />
          </div>
          <div data-id="element-258" data-mp="3e8ufju0pzoh2">
            <SectionHeading eyebrow="How the house is organised" title="Four desks, one contract owner" intro="A single trader owns your contract end to end and coordinates the four internal desks, so you never chase updates across departments." data-id="element-259" data-mp="2qkuth7ecsm8t" />
            <div className="mt-10 grid gap-8 sm:grid-cols-2" data-id="element-260" data-mp="3fbii094s8gqe">
              {teamDesks.map(desk => <div key={desk.title} className="border-l border-gold/40 pl-5" data-id="element-261" data-mp="3k1z046jjsqj3">
                  <h3 className="text-base font-semibold text-bone" data-id="element-262" data-mp="3p1k7948vg56w">{desk.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-263" data-mp="3p3el5ylq6x1h">{desk.text}</p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-hair bg-panel py-16" data-id="element-264" data-mp="2wez6xeaf4ir7">
        <div className="mx-auto grid max-w-shell gap-10 px-6 lg:grid-cols-[1fr_1fr] lg:items-center" data-id="element-265" data-mp="31axz7cyvakww">
          <div data-id="element-266" data-mp="36bvi5rrpbizg">
            <h2 className="font-display text-3xl text-bone" data-id="element-267" data-mp="36cnbsx1qn6n3">Standards we hold ourselves to</h2>
            <ul className="mt-6 grid gap-4" data-id="element-268" data-mp="gl8mb5hq596u">
              {company.certifications.map(item => <li key={item} className="flex gap-3 text-sm text-bone/85" data-id="element-269" data-mp="lja6jtqvzejw">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-270" data-mp="261uh9ed2rjte" />
                  {item}
                </li>)}
            </ul>
          </div>
          <div className="border border-hair bg-ink p-8" data-id="element-271" data-mp="1wcauvyq5l7ts">
            <h2 className="font-display text-2xl text-bone" data-id="element-272" data-mp="1waq32gv842zm">Visit or verify us</h2>
            <div className="mt-6 space-y-5 text-sm text-muted" data-id="element-273" data-mp="1rdpy69a416a3">
              {company.offices.map(office => <div key={office.label} data-id="element-274" data-mp="1na1onl1x40m7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-gold" data-id="element-275" data-mp="1id1plu3hjy4l">
                    {office.label}
                  </p>
                  {office.lines.map(line => <p key={line} className="mt-1 text-bone/85" data-id="element-276" data-mp="18g6vdne8wjw4">
                      {line}
                    </p>)}
                </div>)}
            </div>
            <Link to="/contact" className="mt-7 inline-flex items-center gap-2 rounded-sm bg-gold px-6 py-3.5 text-sm font-semibold text-ink transition-colors duration-150 hover:bg-gold-light" data-id="element-277" data-mp="32swoi2wshp2g">
              Contact the trading desk
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-278" data-mp="22vh33zp3jtxz" />
            </Link>
          </div>
        </div>
      </section>
    </>;
}